import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import api from '../../apiSingleton';
import history from '../../history';

function buildUrlString(location) {
    // Send only path + query to the API (no domain)
    if (location) {
        const path = location.pathname || '/';
        const search = location.search || '';
        const hash = location.hash || '';

        return `${path}${search}${hash}` || '/';
    }
    if (typeof window !== 'undefined') {
        const path = window.location.pathname || '/';
        const search = window.location.search || '';
        const hash = window.location.hash || '';

        return `${path}${search}${hash}` || '/';
    }

    return '/';
}

// Normalize various possible API response shapes into a flat
// { title, description, keywords, canonical, has: { ... } }
function normalizeMetaResponse(resp) {
    if (!resp) return null;

    // Unwrap common containers
    let data = resp;

    if (Array.isArray(resp)) data = resp[0] || null;
    if (data && typeof data === 'object' && data.data && typeof data.data === 'object') {
        data = data.data;
    }

    if (!data || typeof data !== 'object') return null;

    // STRICT mapping as requested:
    // title        => nameUa
    // description  => descriptionUa
    // keywords     => keywords
    // canonical    => canonical
    const title = data.nameUa !== undefined && data.nameUa !== null ? String(data.nameUa) : undefined;
    const description = data.descriptionUa !== undefined && data.descriptionUa !== null ? String(data.descriptionUa) : undefined;
    const keywords = data.keywords !== undefined && data.keywords !== null ? String(data.keywords) : undefined;
    const canonical = data.canonical !== undefined && data.canonical !== null ? String(data.canonical) : undefined;

    const has = {
        title       : Object.prototype.hasOwnProperty.call(data, 'nameUa'),
        description : Object.prototype.hasOwnProperty.call(data, 'descriptionUa'),
        keywords    : Object.prototype.hasOwnProperty.call(data, 'keywords'),
        canonical   : Object.prototype.hasOwnProperty.call(data, 'canonical')
    };

    return {
        title       : typeof title === 'string' ? title.trim() : '',
        description : typeof description === 'string' ? description.trim() : '',
        keywords    : typeof keywords === 'string' ? keywords.trim() : '',
        canonical   : typeof canonical === 'string' ? canonical.trim() : '',
        has
    };
}

function MetaTags() {
    const location = useLocation();
    const [ meta, setMeta ] = useState(null);
    const [ isCleaned, setIsCleaned ] = useState(false);
    const latestUrlRef = useRef('');
    const requestSeqRef = useRef(0);
    const [ navTick, setNavTick ] = useState(0);

    const currentUrl = useMemo(() => buildUrlString(location), [ location, navTick ]);

    // Extra safety: listen to history changes and hash changes to re-trigger meta updates
    useEffect(() => {
        const unlisten = history && history.listen ? history.listen(() => {
            setNavTick(t => t + 1);
        }) : null;

        const onHashChange = () => setNavTick(t => t + 1);

        if (typeof window !== 'undefined') window.addEventListener('hashchange', onHashChange);

        return () => {
            if (unlisten) unlisten();
            if (typeof window !== 'undefined') window.removeEventListener('hashchange', onHashChange);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        // track the latest URL to avoid applying stale responses after fast navigation

        latestUrlRef.current = currentUrl;
        const requestId = ++requestSeqRef.current;
        // reset meta for the new URL so that we re-render with fresh values only

        setMeta(null);

        async function fetchMeta() {
            try {
                const raw = await api.meta.byUrl(currentUrl);

                const normalized = normalizeMetaResponse(raw);

                if (!cancelled) {
                    // Ignore if a newer request has been started or URL already changed
                    if (requestId !== requestSeqRef.current || latestUrlRef.current !== currentUrl) return;
                    if (process.env.NODE_ENV !== 'production') {
                        // eslint-disable-next-line no-console
                        console.debug('[MetaTags] fetched', { url: currentUrl, raw, normalized });
                    }
                    setMeta(normalized);
                }
            } catch (e) {
                if (!cancelled) {
                    if (requestId !== requestSeqRef.current || latestUrlRef.current !== currentUrl) return;
                    setMeta(null);
                }
            }
        }
        fetchMeta();

        return () => {
            cancelled = true;
        };
    }, [ currentUrl ]);

    // Phase 1: FORCE cleanup on every navigation BEFORE Helmet render,
    // so existing tags from templates/components are removed consistently.
    useLayoutEffect(() => {
        setIsCleaned(false);

        const removeAll = selector => {
            const nodes = document.head ? document.head.querySelectorAll(selector) : [];

            nodes.forEach(n => n.parentNode && n.parentNode.removeChild(n));
        };

        // Always remove these tags on route change; new values will be applied from API response
        removeAll('title');
        try {
            document.title = '';
        } catch (_) { /* noop */ }

        removeAll('meta[name="description"]');
        removeAll('meta[name="keywords"]');
        removeAll('link[rel="canonical"]');

        setIsCleaned(true);

        return () => {
            setIsCleaned(false);
        };
    }, [ currentUrl ]);

    const title = meta?.title || '';
    const description = meta?.description || '';
    const keywords = meta?.keywords || '';
    const canonical = meta?.canonical || '';

    // Ensure document.title is explicitly set after cleanup when we have a title
    // Do it in both layout and passive effects to win over any late page-level Helmet/effects on first load
    useLayoutEffect(() => {
        if (!isCleaned) return;
        if (meta && meta.has && meta.has.title && title) {
            try {
                document.title = title;
            } catch (_) { /* noop */ }
        }
    }, [ isCleaned, title, meta ]);

    useEffect(() => {
        if (!isCleaned) return;
        if (!(meta && meta.has && meta.has.title && title)) return;

        let raf = 0;

        let tm = 0;
        // Try to set once more on next macrotask and next animation frame

        try {
            tm = window.setTimeout(() => {
                try {
                    document.title = title;
                } catch (_) { /* noop */ }
            }, 0);

            raf = window.requestAnimationFrame(() => {
                try {
                    document.title = title;
                } catch (_) { /* noop */ }
            });
        } catch (_) { /* noop */ }

        return () => {
            if (tm) window.clearTimeout(tm);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [ isCleaned, title, meta ]);

    // Phase 2: render Helmet ONLY after cleanup is done
    return isCleaned ? (
        <Helmet defer={false} key={`helmet-${currentUrl}`}>
            {title ? <title key='meta-title'>{title}</title> : null}
            {description ? (
                <meta key='meta-description' name='description' content={description} />
            ) : null}
            {keywords ? (
                <meta key='meta-keywords' name='keywords' content={keywords} />
            ) : null}
            {canonical ? (
                <link key='meta-canonical' rel='canonical' href={canonical} />
            ) : null}
        </Helmet>
    ) : null;
}

export default MetaTags;
